import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Box,
  CircularProgress,
  IconButton,
  makeStyles,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Grid,
  TextField,
  Paper,
  Tooltip,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import Alert from '@material-ui/lab/Alert';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Header,
  Page,
  Content,
  MarkdownContent,
} from '@backstage/core-components';

interface Release {
  id: number;
  version: string;
  current: boolean;
  changelog: string;
}

interface GqlResponse<T> {
  data?: T;
  errors?: { message: string }[];
}

const useStyles = makeStyles((theme) => ({
  table: {
    '& th': {
      fontWeight: 600,
    },
  },
  currentChip: {
    marginLeft: theme.spacing(1),
  },
  actions: {
    whiteSpace: 'nowrap',
  },
  deletePreview: {
    maxHeight: 400,
    overflow: 'auto',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    backgroundColor: theme.palette.background.default,
    marginTop: theme.spacing(2),
  },
  editorInput: {
    '& .MuiOutlinedInput-root': {
      alignItems: 'flex-start',
    },
    '& .MuiOutlinedInput-inputMultiline': {
      maxHeight: '50vh',
      overflow: 'auto !important',
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
  },
  previewPane: {
    maxHeight: '50vh',
    overflow: 'auto',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
  },
  paneLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    flexShrink: 0,
  },
}));

export const ReleaseVersionsPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const config = useApi(configApiRef);
  const { fetch } = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  const graphqlUrl = `${backendUrl}/api/proxy/release-notes/graphql`;

  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [settingCurrent, setSettingCurrent] = useState<string | null>(null);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<Release | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Edit dialog
  const [editTarget, setEditTarget] = useState<Release | null>(null);
  const [editChangelog, setEditChangelog] = useState('');
  const [saving, setSaving] = useState(false);

  // Loading release details for dialogs
  const [loadingRelease, setLoadingRelease] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const gql = useCallback(
    async <T,>(query: string, variables?: Record<string, unknown>): Promise<GqlResponse<T>> => {
      const res = await fetch(graphqlUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables }),
      });
      return res.json();
    },
    [fetch, graphqlUrl],
  );

  const fetchReleases = useCallback(async () => {
    try {
      const [versionsResult, currentResult] = await Promise.all([
        gql<{ releaseVersions: string[] }>(`query { releaseVersions }`),
        gql<{ currentRelease: { version: string } | null }>(`query { currentRelease { version } }`),
      ]);

      const versions = versionsResult.data?.releaseVersions ?? [];
      const currentVer = currentResult.data?.currentRelease?.version ?? null;

      setReleases(
        versions.map((v, i) => ({
          id: i,
          version: v,
          current: v === currentVer,
          changelog: '',
        })),
      );
    } catch {
      setSnackbar({ open: true, message: 'Failed to load versions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [gql]);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  const fetchRelease = async (version: string): Promise<Release | null> => {
    setLoadingRelease(version);
    try {
      const result = await gql<{ release: Release | null }>(`
        query GetRelease($version: String!) {
          release(version: $version) { id version current changelog }
        }
      `, { version });
      return result.data?.release ?? null;
    } catch {
      setSnackbar({ open: true, message: 'Failed to load release details', severity: 'error' });
      return null;
    } finally {
      setLoadingRelease(null);
    }
  };

  const handleSetCurrent = async (version: string) => {
    setSettingCurrent(version);
    try {
      const result = await gql<{ setCurrentRelease: { version: string } }>(`
        mutation SetCurrentRelease($version: String!) {
          setCurrentRelease(version: $version) { version }
        }
      `, { version });
      if (result.errors) {
        setSnackbar({ open: true, message: `Failed: ${result.errors[0].message}`, severity: 'error' });
        return;
      }
      setReleases((prev) =>
        prev.map((r) => ({ ...r, current: r.version === version })),
      );
      setSnackbar({ open: true, message: `Set ${version} as current`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed: network error', severity: 'error' });
    } finally {
      setSettingCurrent(null);
    }
  };

  const openDeleteDialog = async (version: string) => {
    const release = await fetchRelease(version);
    if (release) setDeleteTarget(release);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await gql<{ deleteRelease: { version: string } }>(`
        mutation DeleteRelease($version: String!) {
          deleteRelease(version: $version) { version }
        }
      `, { version: deleteTarget.version });
      if (result.errors) {
        setSnackbar({ open: true, message: `Delete failed: ${result.errors[0].message}`, severity: 'error' });
        return;
      }
      setReleases((prev) => prev.filter((r) => r.version !== deleteTarget.version));
      setSnackbar({ open: true, message: `Deleted ${deleteTarget.version}`, severity: 'success' });
      setDeleteTarget(null);
    } catch {
      setSnackbar({ open: true, message: 'Delete failed: network error', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const openEditDialog = async (version: string) => {
    const release = await fetchRelease(version);
    if (release) {
      setEditTarget(release);
      setEditChangelog(release.changelog);
    }
  };

  const handleSave = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      const result = await gql<{ editRelease: { version: string; changelog: string } }>(`
        mutation EditRelease($version: String!, $changelog: String!) {
          editRelease(version: $version, changelog: $changelog) { version changelog }
        }
      `, { version: editTarget.version, changelog: editChangelog });
      if (result.errors) {
        setSnackbar({ open: true, message: `Save failed: ${result.errors[0].message}`, severity: 'error' });
        return;
      }
      setSnackbar({ open: true, message: `Updated ${editTarget.version}`, severity: 'success' });
      setEditTarget(null);
    } catch {
      setSnackbar({ open: true, message: 'Save failed: network error', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header
        title="Release Versions"
        subtitle="Manage published release notes"
      />
      <Content>
        <Box mb={2}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/release-notes')}
          >
            Back to Generator
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : releases.length === 0 ? (
          <Typography color="textSecondary">No versions found.</Typography>
        ) : (
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {releases.map((r) => (
                <TableRow key={r.version}>
                  <TableCell>
                    <Typography variant="body2">{r.version}</Typography>
                  </TableCell>
                  <TableCell>
                    {r.current && (
                      <Chip
                        label="current"
                        size="small"
                        color="primary"
                        icon={<CheckCircleIcon />}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.actions}>
                    {!r.current && (
                      <Tooltip title="Set as current">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleSetCurrent(r.version)}
                            disabled={settingCurrent !== null}
                          >
                            {settingCurrent === r.version ? (
                              <CircularProgress size={18} />
                            ) : (
                              <RadioButtonUncheckedIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(r.version)}
                          disabled={loadingRelease !== null}
                        >
                          {loadingRelease === r.version ? (
                            <CircularProgress size={18} />
                          ) : (
                            <EditIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(r.version)}
                          disabled={loadingRelease !== null}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteTarget !== null}
          onClose={() => !deleting && setDeleteTarget(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Delete Release {deleteTarget?.version}?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              This will permanently delete this release. The release notes for this version are shown below:
            </Typography>
            {deleteTarget && (
              <div className={classes.deletePreview}>
                <MarkdownContent content={deleteTarget.changelog} />
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleDelete}
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog
          open={editTarget !== null}
          onClose={() => !saving && setEditTarget(null)}
          maxWidth="xl"
          fullWidth
        >
          <DialogTitle>Edit Release {editTarget?.version}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" className={classes.paneLabel}>
                  Edit
                </Typography>
                <TextField
                  className={classes.editorInput}
                  multiline
                  fullWidth
                  variant="outlined"
                  value={editChangelog}
                  onChange={(e) => setEditChangelog(e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" className={classes.paneLabel}>
                  Preview
                </Typography>
                <Paper className={classes.previewPane} variant="outlined">
                  {editChangelog.trim() ? (
                    <MarkdownContent content={editChangelog} />
                  ) : (
                    <Typography color="textSecondary" variant="body2">
                      Start typing to see a preview...
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditTarget(null)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || !editChangelog.trim()}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Content>
    </Page>
  );
};
